import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import type { Database } from "@/integrations/supabase/types";
import type { Product } from "@/lib/shop";

function publicClient() {
  const key = process.env.SUPABASE_PUBLISHABLE_KEY!;
  return createClient<Database>(process.env.SUPABASE_URL!, key, {
    auth: { persistSession: false },
    global: {
      fetch: (input, init) => {
        const h = new Headers(init?.headers);
        if (key.startsWith("sb_") && h.get("Authorization") === `Bearer ${key}`) h.delete("Authorization");
        h.set("apikey", key);
        return fetch(input, { ...init, headers: h });
      },
    },
  });
}

function normalise(row: any): Product {
  return {
    ...row,
    standard_price: Number(row.standard_price ?? 0),
    physio_price: row.physio_price == null ? null : Number(row.physio_price),
    sale_price: row.sale_price == null ? null : Number(row.sale_price),
    images: row.images ?? [],
    features: row.features ?? [],
    specifications: Array.isArray(row.specifications) ? row.specifications : [],
    variants: Array.isArray(row.variants) ? row.variants : [],
    suitable_for: row.suitable_for ?? [],
    tags: row.tags ?? [],
    body_parts: row.body_parts ?? [],
    conditions: row.conditions ?? [],
    related_slugs: row.related_slugs ?? [],
    bought_together_slugs: row.bought_together_slugs ?? [],
    sides: row.sides ?? [],
    sizes: row.sizes ?? [],
    colours: row.colours ?? [],
  } as Product;
}

export const listProducts = createServerFn({ method: "GET" }).handler(async () => {
  const { data, error } = await publicClient()
    .from("products")
    .select("*")
    .eq("active", true)
    .order("sort_order", { ascending: true })
    .order("created_at", { ascending: false });
  if (error) throw new Error(error.message);
  return (data ?? []).map(normalise);
});

export const getProductBySlug = createServerFn({ method: "GET" })
  .inputValidator((data: { slug: string }) => data)
  .handler(async ({ data }) => {
    const supabase = publicClient();
    const { data: row, error } = await supabase
      .from("products")
      .select("*")
      .eq("slug", data.slug)
      .eq("active", true)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!row) return { product: null, related: [] as Product[], boughtTogether: [] as Product[] };

    const product = normalise(row);
    const slugs = [...product.related_slugs, ...product.bought_together_slugs].filter(Boolean);
    let others: Product[] = [];
    if (slugs.length) {
      const { data: rel } = await supabase.from("products").select("*").in("slug", slugs).eq("active", true);
      others = (rel ?? []).map(normalise);
    }
    return {
      product,
      related: others.filter((o) => product.related_slugs.includes(o.slug)),
      boughtTogether: others.filter((o) => product.bought_together_slugs.includes(o.slug)),
    };
  });
